import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import Breadcrumbs from "../../../components/navigation/Breadcrumbs";
import { GoogleMapsLoader } from "../../maps/GoogleMapsLoader";
import { API_BASE_URL } from "../../../config";
import { MAINTENANCE_CONFIG, HYGIENE_CONFIG } from "../../../config/donationServiceConfigs";
import { asErr } from "../../../utils/apiHelpers";
import { getAuthToken, isAuthenticated } from "../../../services/authService";
import * as donorService from "../../../services/donorService";
import { listDonationRequests, type DonationRequestItem } from "../../../services/donationRequestService";
import {
  listSupplyRequestsByDonor,
  getMosqueDonationOpportunityByRequestId,
  flattenDetailPayload,
  getVal,
  type SupplyRequestListItem,
} from "../../../services/mosqueDonationService";
import {
  getHygieneDdrById,
  listHygieneSupplyRequestsByDonor,
  type HygieneSupplyRequestListItem,
} from "../../../services/hygieneDonationService";
import {
  listHygieneSupplyFiles,
  listSupplyFiles,
  uploadSupplyRequestFile,
  downloadSupplyFile,
  type SupplyRequestFileRow,
} from "../../../services/supplyRequestFilesService";
import type { DonorRow } from "../../../types/donor";
import SupplyRequestsSection from "./SupplyRequestsSection";
import NotBenefactorModal from "../../NotBenefactorModal";

type MosqueDonationRequestDetail = {
  request_id?: number;
  request_number?: string | number;
  request_name?: string;
  mosque_id?: number;
  mosqueId?: number;
  mosque_name?: string;
  mosqueName?: string;
  mosque_number?: string | number;
  number?: string | number;
  mosqueNumber?: string | number;
  serialNumber?: string | number;
  status?: string | null;
  full_address?: string | null;
  fullAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  classification_name?: string | null;
  classificationName?: string | null;
  region_name?: string | null;
  regionName?: string | null;
  city_name?: string | null;
  cityName?: string | null;
  moia_center_name?: string | null;
  centerName?: string | null;
  district_name?: string | null;
  districtName?: string | null;
  neighborhoodName?: string | null;
  capacity?: number | null;
  women_prayer_room_capacity?: number | null;
  has_women_prayer_room?: string | null;
  hasWomenPrayerRoom?: boolean | string | null;
  cat_a_name?: string | null;
  cat_b_name?: string | null;
  cat_c_name?: string | null;
  cat_c_names?: string | null;
  cat_d_name?: string | null;
  cat_e_names?: string | null;
};

type NavState = {
  item?: MosqueDonationRequestDetail;
  fromMyRequests?: boolean;
  donorToken?: string;
  source?: "maintenance" | "hygiene";
  backTo?: string;
  /** رقم الطلب (مثل MSR-00000172) عند الدخول من طلباتي - عقود الصيانة */
  contributionSerial?: string | null;
} | null;

type SectionId = "all" | "mosque" | "applicant" | "requests";
type SectionNavItem = { id: SectionId; label: string };

const buildGoogleMapsLink = (lat?: number | null, lng?: number | null) => {
  if (lat == null || lng == null) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&hl=ar&z=16`;
};

const yesNoAr = (v?: string | boolean | null) => {
  if (v === true) return "نعم";
  if (v === false) return "لا";
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return "—";
  return ["1", "true", "t", "yes", "y", "نعم", "ok", "✓"].includes(s) ? "نعم" : "لا";
};

const getBaseURL = () => {
  if (process.env.NODE_ENV === "development") return "";
  return API_BASE_URL;
};

const numberSar = (v: number | undefined | null) =>
  typeof v === "number" && !isNaN(v) ? v.toLocaleString("en-US") : "-";

const normalizeText = (value: unknown) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return text;
};

const stageBadge = (stage?: string) => {
  const s = (stage || "").toLowerCase();
  if (s.includes("draft")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200">
        مسودة
      </span>
    );
  }
  if (s.includes("progress")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-sky-50 text-sky-700 ring-1 ring-sky-200">
        قيد المعالجة
      </span>
    );
  }
  if (s.includes("submitted")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-50 text-amber-800 ring-1 ring-amber-200">
        تم الإرسال
      </span>
    );
  }
  if (s.includes("approved") || s.includes("done")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
        معتمد
      </span>
    );
  }
  if (s.includes("reject")) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-red-200">
        مرفوض
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-neutral-50 text-neutral-800 ring-1 ring-neutral-200">
      {stage || "غير معروف"}
    </span>
  );
};


type SupplyRow = SupplyRequestListItem | HygieneSupplyRequestListItem;

const MosqueDonationRequest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state as NavState) || null;
  const initialItem = navState?.item ?? null;
  const fromMyRequests = navState?.fromMyRequests === true;
  const donorTokenFromState = navState?.donorToken ?? "";
  const isHygiene = navState?.source === "hygiene";
  const serviceConfig = isHygiene ? HYGIENE_CONFIG : MAINTENANCE_CONFIG;
  const backTo = navState?.backTo ?? "/my-requests";

  const [loading, setLoading] = useState(!initialItem);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<MosqueDonationRequestDetail | null>(initialItem);
  const [activeSection, setActiveSection] = useState<SectionId>("all");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [donors, setDonors] = useState<DonorRow[]>([]);
  const [selectedDonorToken, setSelectedDonorToken] = useState<string>(donorTokenFromState);
  const [loadingDonors, setLoadingDonors] = useState(false);
  const [showNotBenefactorModal, setShowNotBenefactorModal] = useState(false);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [requests, setRequests] = useState<DonationRequestItem[]>([]);
  const [mosqueRequests, setMosqueRequests] = useState<SupplyRow[]>([]);
  const [loadingMosqueReqs, setLoadingMosqueReqs] = useState(false);
  const [mosqueReqsError, setMosqueReqsError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const mapRef = useRef<HTMLDivElement | null>(null);

  // Supply requests section: selection, files, upload, submit
  const [selectedSupplyReq, setSelectedSupplyReq] = useState<SupplyRow | null>(null);
  const [supplyFiles, setSupplyFiles] = useState<SupplyRequestFileRow[]>([]);
  const [supplyFilesLoading, setSupplyFilesLoading] = useState(false);
  const [supplyPreviews, setSupplyPreviews] = useState<Record<number, string>>({});
  const [supplyEditing, setSupplyEditing] = useState(false);
  const [supplyDocType, setSupplyDocType] = useState<number>(1);
  const [supplyUploadBusy, setSupplyUploadBusy] = useState(false);
  const [supplyUploadErr, setSupplyUploadErr] = useState<string | null>(null);
  const [supplySubmitErr, setSupplySubmitErr] = useState<string | null>(null);
  const [supplySubmitOk, setSupplySubmitOk] = useState<string | null>(null);
  const [supplyDeclarationAccepted, setSupplyDeclarationAccepted] = useState(false);
  const [supplySubmitting, setSupplySubmitting] = useState(false);
  const supplyFileInputRef = useRef<HTMLInputElement | null>(null);
  const MAX_MB = 10;
  const MAX_BYTES = MAX_MB * 1024 * 1024;
  const ACCEPT_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!id) return;
      try {
        if (!initialItem) setLoading(true);
        setErr(null);
        const payload = isHygiene
          ? await getHygieneDdrById(id)
          : await getMosqueDonationOpportunityByRequestId(Number(id));
        if (!alive) return;
        const raw = payload && typeof payload === "object" && !Array.isArray(payload) ? payload : null;
        const merged = raw ? flattenDetailPayload(raw) : null;
        if (merged && Object.keys(merged).length > 0) {
          setData((prev) => (prev ? { ...prev, ...merged } : (merged as MosqueDonationRequestDetail)));
        }
      } catch (e) {
        if (!alive) return;
        if (!initialItem) setErr(asErr(e, "Failed to load details").message);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, initialItem, isHygiene]);

  useEffect(() => {
    if (!data?.latitude || !data?.longitude || activeSection !== "mosque") return;
    let map: google.maps.Map | null = null;
    GoogleMapsLoader.load("ar", "SA")
      .then((google) => {
        if (!mapRef.current) return;
        const center = { lat: Number(data.latitude), lng: Number(data.longitude) };
        map = new google.maps.Map(mapRef.current, {
          center,
          zoom: 14,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: true,
          zoomControl: true,
        });
        new google.maps.Marker({ position: center, map });
      })
      .catch(() => {});
    return () => {
      map = null;
    };
  }, [data?.latitude, data?.longitude, activeSection]);

  const loggedIn = isAuthenticated();

  useEffect(() => {
    if (!loggedIn) {
      setDonors([]);
      setSelectedDonorToken("");
      setRequests([]);
      setRequestsError(null);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        setLoadingDonors(true);
        setRequestsError(null);
        const ds = donorService.scopeDonorsToCurrentUser(
          (await donorService.listMine()) as DonorRow[]
        );
        if (!mounted) return;
        setDonors(ds);
        if (ds.length === 0) setShowNotBenefactorModal(true);
        const initial =
          donorTokenFromState && ds.some((d) => d.donorIdToken === donorTokenFromState)
            ? donorTokenFromState
            : ds[0]?.donorIdToken || "";
        setSelectedDonorToken(initial);
      } catch (e) {
        if (!mounted) return;
        setRequestsError("تعذر تحميل بيانات المستفيد");
      } finally {
        if (mounted) setLoadingDonors(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [loggedIn, donorTokenFromState]);

  useEffect(() => {
    if (!selectedDonorToken) {
      setRequests([]);
      return;
    }
    let closed = false;
    (async () => {
      try {
        setLoadingReqs(true);
        const rows = await listDonationRequests(selectedDonorToken, undefined);
        if (closed) return;
        const sorted = [...rows].sort((a, b) => {
          const na = parseInt(String(a.code || "").replace(/\D+/g, "") || "0", 10);
          const nb = parseInt(String(b.code || "").replace(/\D+/g, "") || "0", 10);
          return nb - na;
        });
        setRequests(sorted);
      } catch {
        if (!closed) setRequestsError("تعذر تحميل الطلبات");
      } finally {
        if (!closed) setLoadingReqs(false);
      }
    })();
    return () => {
      closed = true;
    };
  }, [selectedDonorToken]);

  useEffect(() => {
    if (!loggedIn || !selectedDonorToken) {
      setMosqueRequests([]);
      setMosqueReqsError(null);
      return;
    }
    let closed = false;
    (async () => {
      try {
        setLoadingMosqueReqs(true);
        setMosqueReqsError(null);
        const result = isHygiene
          ? await listHygieneSupplyRequestsByDonor({
              DonorIdToken: selectedDonorToken,
              PageNo: 1,
              PageSize: 100,
            })
          : await listSupplyRequestsByDonor({
              DonorIdToken: selectedDonorToken,
              PageNo: 1,
              PageSize: 100,
            });
        if (!closed) setMosqueRequests(result.data ?? []);
      } catch (e) {
        if (!closed) {
          setMosqueReqsError(asErr(e, "تعذر تحميل طلبات المساجد").message);
          setMosqueRequests([]);
        }
      } finally {
        if (!closed) setLoadingMosqueReqs(false);
      }
    })();
    return () => {
      closed = true;
    };
  }, [loggedIn, selectedDonorToken, isHygiene]);

  const filteredRequests = useMemo(() => {
    if (!q.trim()) return requests;
    const needle = q.trim().toLowerCase();
    return requests.filter(
      (r) =>
        (r.code || "").toLowerCase().includes(needle) ||
        (r.landCode || "").toLowerCase().includes(needle) ||
        (r.stageName || r.state || "").toLowerCase().includes(needle)
    );
  }, [q, requests]);

  const selectedDonor = useMemo(
    () => donors.find((d) => d.donorIdToken === selectedDonorToken) || null,
    [donors, selectedDonorToken]
  );

  // Filter supply requests by current DDR (id from URL)
  const filteredSupplyRequests = useMemo(() => {
    const ddrId = id ? Number(id) : NaN;
    if (!Number.isFinite(ddrId)) return mosqueRequests;
    return mosqueRequests.filter(
      (r) => (r.ddrId ?? (r as any).ddrId) === ddrId
    );
  }, [id, mosqueRequests]);

  // Auto-select first supply request when filtered list changes
  useEffect(() => {
    if (filteredSupplyRequests.length > 0 && !selectedSupplyReq) {
      setSelectedSupplyReq(filteredSupplyRequests[0]);
    } else if (
      selectedSupplyReq &&
      !filteredSupplyRequests.some((r) => r.supplyId === selectedSupplyReq.supplyId)
    ) {
      setSelectedSupplyReq(filteredSupplyRequests[0] ?? null);
    }
  }, [filteredSupplyRequests, selectedSupplyReq]);

  async function fetchSupplyFilesForSelected() {
    if (!selectedSupplyReq?.supplyId) {
      setSupplyFiles([]);
      setSupplyPreviews({});
      return;
    }
    try {
      setSupplyFilesLoading(true);
      const list = isHygiene
        ? await listHygieneSupplyFiles(selectedSupplyReq.supplyId)
        : await listSupplyFiles(selectedSupplyReq.supplyId);
      const map = new Map<number | string, SupplyRequestFileRow>();
      (Array.isArray(list) ? list : []).forEach((r) => {
        const key = r?.id ?? r?.attachmentId ?? `${r.name}-${r.documentTypeId}`;
        map.set(key, r);
      });
      setSupplyFiles([...map.values()]);
    } catch {
      setSupplyFiles([]);
    } finally {
      setSupplyFilesLoading(false);
    }
  }

  /** طلبات الصيانة والنظافة لا تتطلب مرفقات — لا حاجة لتحميل الملفات */
  const supplyAttachmentsRequired = false;

  useEffect(() => {
    if (!selectedSupplyReq?.supplyId) {
      setSupplyFiles([]);
      setSupplyPreviews({});
      return;
    }
    setSupplyUploadErr(null);
    setSupplyEditing(false);
    if (!supplyAttachmentsRequired) {
      setSupplyFiles([]);
      setSupplyPreviews({});
      return;
    }
    fetchSupplyFilesForSelected();
  }, [selectedSupplyReq?.supplyId, isHygiene, supplyAttachmentsRequired]);

  async function supplyDownloadByDonor(f: SupplyRequestFileRow) {
    const { blob, filename } = await downloadSupplyFile(f);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    let closed = false;
    async function load() {
      if (!selectedSupplyReq?.supplyId) {
        setSupplyPreviews({});
        return;
      }
      const next: Record<number, string> = {};
      for (const f of supplyFiles) {
        try {
          const { blob } = await downloadSupplyFile(f);
          if (closed) return;
          const contentType = (blob as Blob & { type?: string })?.type || "";
          if (!contentType.startsWith("image/")) continue;
          next[f.id] = URL.createObjectURL(blob);
        } catch {
          /* ignore */
        }
      }
      if (!closed) setSupplyPreviews(next);
    }
    load();
    return () => {
      closed = true;
      Object.values(supplyPreviews).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [supplyFiles, selectedSupplyReq?.supplyId]);

  function stripDataUrl(s: string) {
    const i = s.indexOf("base64,");
    return i >= 0 ? s.substring(i + 7) : s;
  }
  function fileToBase64(file: File) {
    return new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ""));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    });
  }

  async function supplyDoUpload(file: File, type: number) {
    if (!selectedSupplyReq) return;
    const DOC = { FinancialCapacity: 1, PowerOfAttorney: 2 } as const;
    if (supplyFiles.some((f) => f.documentTypeId === type)) {
      setSupplyUploadErr("تم رفع هذا النوع من المستندات مسبقًا");
      return;
    }
    if (!ACCEPT_MIME.includes(file.type) || file.size > MAX_BYTES) {
      setSupplyUploadErr(
        `يرجى تحميل ملف بصيغة مسموح بها ولا يتجاوز حجمه ${MAX_MB} ميجابايت`
      );
      return;
    }
    const dataUrl = await fileToBase64(file);
    await uploadSupplyRequestFile({
      supplyId: selectedSupplyReq.supplyId,
      fileName: file.name,
      documentType: type,
      description: "",
      fileBase64: stripDataUrl(dataUrl),
    });
    await fetchSupplyFilesForSelected();
  }

  async function reloadMosqueRequests() {
    if (!selectedDonorToken) return;
    try {
      setLoadingMosqueReqs(true);
      const result = isHygiene
        ? await listHygieneSupplyRequestsByDonor({
            DonorIdToken: selectedDonorToken,
            PageNo: 1,
            PageSize: 100,
          })
        : await listSupplyRequestsByDonor({
            DonorIdToken: selectedDonorToken,
            PageNo: 1,
            PageSize: 100,
          });
      setMosqueRequests(result.data ?? []);
    } catch {
      setMosqueReqsError("تعذر تحميل الطلبات");
    } finally {
      setLoadingMosqueReqs(false);
    }
  }

  const DOC = { FinancialCapacity: 1, PowerOfAttorney: 2 } as const;
  const supplyHasFinancial = supplyFiles.some(
    (f) => f.documentTypeId === DOC.FinancialCapacity
  );
  const supplyHasPoA = supplyFiles.some(
    (f) => f.documentTypeId === DOC.PowerOfAttorney
  );
  const supplyStateStr = (
    selectedSupplyReq?.state ?? selectedSupplyReq?.status ?? ""
  ).toLowerCase();
  const supplyIsDraft =
    supplyStateStr.includes("draft") || supplyStateStr.includes("مسودة");
  const donorType = (selectedDonor?.donorType ?? "direct").toLowerCase();
  const supplyIsDirectDonor = donorType === "direct";
  /** طلبات الصيانة والنظافة لا تتطلب مرفقات — يكفي أن يكون الطلب مسودة */
  const supplyRequirementsMet = supplyIsDraft;

  useEffect(() => {
    setSupplyDeclarationAccepted(false);
  }, [activeSection, selectedSupplyReq?.supplyId]);

  async function onSubmitSupplyRequest() {
    if (!selectedSupplyReq) return;
    setSupplySubmitErr(null);
    setSupplySubmitOk(null);
    try {
      setSupplySubmitting(true);
      const baseUrl =
        process.env.NODE_ENV === "development" ? "" : API_BASE_URL;
      const path = serviceConfig.sendPath;
      const tok = getAuthToken?.();
      await axios.patch(
        `${baseUrl}${path}`,
        {
          supplyId: selectedSupplyReq.supplyId,
          donorToken: selectedDonorToken,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
          },
        }
      );
      setSupplySubmitOk("تم إرسال الطلب بنجاح");
      await reloadMosqueRequests();
      await fetchSupplyFilesForSelected();
      navigate(
        `/my-requests?donor=${encodeURIComponent(selectedDonorToken)}&tab=${serviceConfig.tabId}`,
        { replace: true }
      );
    } catch {
      setSupplySubmitErr("تعذر إرسال الطلب. يرجى المحاولة مرة أخرى");
    } finally {
      setSupplySubmitting(false);
    }
  }

  const reloadRequests = async (donorToken: string) => {
    try {
      setLoadingReqs(true);
      const rows = await listDonationRequests(donorToken, undefined);
      const sorted = [...rows].sort((a, b) => {
        const na = parseInt(String(a.code || "").replace(/\D+/g, "") || "0", 10);
        const nb = parseInt(String(b.code || "").replace(/\D+/g, "") || "0", 10);
        return nb - na;
      });
      setRequests(sorted);
    } finally {
      setLoadingReqs(false);
    }
  };

  const sectionNav: SectionNavItem[] = [
    { id: "all", label: "عرض كل الطلبات" },
    { id: "mosque", label: "بيانات المسجد" },
    { id: "applicant", label: "بيانات مقدم الطلب" },
    { id: "requests", label: "تفاصيل الطلب" },
  ];
  const contributionNumber =
    normalizeText(data?.request_name) ||
    (data?.request_id != null ? `DDR${data.request_id}` : "");
  const donationSerial = contributionNumber || "-";
  const requestNumber = donationSerial;
  const mosqueSerial =
    data?.mosque_number ?? data?.number ?? data?.mosqueNumber ?? "-";
  const mosqueName = data?.mosque_name || "-";
  const requestStatus = data?.status || "Draft";
  const categoryA = normalizeText(data?.cat_a_name) || "-";
  const categorySummary = (() => {
    const parts = [
      { label: "A", value: data?.cat_a_name },
      { label: "B", value: data?.cat_b_name },
      { label: "C", value: data?.cat_c_name },
      { label: "D", value: data?.cat_d_name },
      { label: "E", value: data?.cat_e_names },
    ]
      .map((part) => ({
        label: part.label,
        value: normalizeText(part.value),
      }))
      .filter((part) => part.value);

    if (parts.length === 0) return "-";
    return parts.map((part) => `${part.label}: ${part.value}`).join(" • ");
  })();

  const sectionLabels: Record<SectionId, string> = {
    all: "عرض كل الطلبات",
    mosque: "بيانات المسجد",
    applicant: "بيانات مقدم الطلب",
    requests: "تفاصيل الطلب",
  };

  const mosqueInfo = useMemo(() => {
    if (!data) return null;
    const g = (...keys: string[]) => {
      const v = getVal(data, ...keys);
      return v !== undefined && v !== null && v !== "" ? v : undefined;
    };
    const mosqueName = g("Mosque_Name", "mosque_Name", "mosque_name", "mosqueName") || "—";
    const mosqueNumber = g("Number", "number", "Mosque_Number", "mosque_Number", "mosque_number", "mosqueNumber", "serialNumber", "Serial_Number") ?? "—";
    const contributionSerial =
      (fromMyRequests && !isHygiene && navState?.contributionSerial)
        ? navState.contributionSerial
        : g("Request_Number", "request_number", "Request_Name", "request_name")
            ?? (data.request_id != null ? `DDR${data.request_id}` : null)
            ?? (id ? `DDR${id}` : "—");
    const classification = g("Classification_Name", "classification_Name", "classification_name", "classificationName") || "—";
    const region = g("Region_Name", "region_Name", "region_name", "regionName") || "—";
    const city = g("City_Name", "city_Name", "city_name", "cityName") || "—";
    const center = g("Moia_Center_Name", "moia_Center_Name", "moia_center_name", "centerName") || "—";
    const district = g("District_Name", "district_Name", "district_name", "districtName", "neighborhoodName", "Neighborhood_Name") || "—";
    const fullAddress = g("Full_Address", "full_Address", "full_address", "fullAddress") || "—";
    const womenSection = yesNoAr(g("Has_Women_Prayer_Room", "has_women_prayer_room", "hasWomenPrayerRoom"));
    const capacity = g("Capacity", "capacity") ?? "—";
    const womenCapacity = g("Women_Prayer_Room_Capacity", "women_prayer_room_capacity") ?? "—";
    const catVal = (...keys: string[]) => g(...keys);
    const categoryTags = isHygiene
      ? [
          { label: "A", value: catVal("Cat_A_Name", "cat_A_Name", "cat_a_name", "catAName") },
          { label: "B", value: catVal("Cat_B_Name", "cat_B_Name", "cat_b_name", "catBName") },
          { label: "C", value: catVal("Cat_C_Names", "Cat_C_Name", "cat_c_names", "cat_c_name", "catCName") },
        ]
      : [
          { label: "A", value: catVal("Cat_A_Name", "cat_A_Name", "cat_a_name", "catAName") },
          { label: "B", value: catVal("Cat_B_Name", "cat_B_Name", "cat_b_name", "catBName") },
          { label: "C", value: catVal("Cat_C_Name", "cat_C_Name", "cat_c_name", "catCName") },
          { label: "D", value: catVal("Cat_D_Name", "cat_D_Name", "cat_d_name", "catDName") },
          { label: "E", value: catVal("Cat_E_Names", "Cat_E_Name", "cat_e_Names", "cat_e_names", "catENames") },
        ];
    const mapsUrl = buildGoogleMapsLink(data.latitude, data.longitude);
    const qrValue = mapsUrl || (typeof window !== "undefined" ? window.location.href : "");
    return { mosqueName, mosqueNumber, contributionSerial, classification, region, city, center, district, fullAddress, womenSection, capacity, womenCapacity, categoryTags, qrValue };
  }, [data, isHygiene, id, fromMyRequests, navState]);

  if (loading) {
    return (
      <div className="dga-container py-6 text-center text-neutral-600" dir="rtl">
        <div className="inline-block animate-pulse">جاري التحميل...</div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="dga-container py-6" dir="rtl">
        <div className="rounded-2xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 mb-4">{err}</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="mb-6">
          <Breadcrumbs />
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white/90 shadow-xl overflow-hidden">
          <div className="px-4 md:px-6 py-6 border-b border-gray-100 bg-white/75 backdrop-blur">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-neutral-900">تفاصيل طلب التبرع</h1>
              </div>
              <div className="flex items-center gap-2">
                {fromMyRequests ? (
                  <button
                    type="button"
                    onClick={() => navigate(backTo)}
                    className="inline-flex items-center justify-center rounded-xl border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-sa-500"
                  >
                    رجوع لطلباتي
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    className="inline-flex items-center gap-2 rounded-xl bg-sa-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sa-700 focus:outline-none focus:ring-2 focus:ring-sa-500 focus:ring-offset-1"
                  >
                    تسجيل الخروج
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 py-6 min-w-0">
            <div className="flex flex-col xl:flex-row gap-6">
              <aside className="xl:w-72 flex-shrink-0">
                <nav
                  id="request-sections-nav"
                  className="hidden xl:block sticky top-28 bg-white/80 border border-gray-200 rounded-2xl shadow-sm p-3 space-y-2"
                  aria-label="أقسام الطلب"
                >
                  {sectionNav.map((section) => {
                    const isActive = activeSection === section.id;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => {
                          setActiveSection(section.id);
                          setMobileNavOpen(false);
                        }}
                        className={`w-full text-right text-sm font-semibold rounded-xl px-4 py-3 transition-all duration-200 ${
                          isActive
                            ? "bg-sa-100 text-sa-700 shadow-inner border border-sa-200"
                            : "text-gray-700 hover:bg-gray-100 border border-transparent"
                        }`}
                        aria-current={isActive ? "true" : "false"}
                      >
                        {section.label}
                      </button>
                    );
                  })}
                </nav>

                <div className="lg:hidden bg-white/80 border border-gray-200 rounded-2xl shadow-sm">
                  <button
                    type="button"
                    onClick={() => setMobileNavOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between px-4 py-3 text-right text-sm font-semibold text-gray-700"
                    aria-expanded={mobileNavOpen}
                  >
                    <span>{sectionLabels[activeSection]}</span>
                    <svg
                      className={`w-5 h-5 transition-transform duration-200 ${mobileNavOpen ? "rotate-180" : ""}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {mobileNavOpen && (
                    <div className="border-t border-gray-200">
                      {sectionNav.map((section) => {
                        const isActive = activeSection === section.id;
                        return (
                          <button
                            key={section.id}
                            type="button"
                            onClick={() => {
                              setActiveSection(section.id);
                              setMobileNavOpen(false);
                            }}
                            className={`w-full text-right text-sm font-semibold px-4 py-3 transition-all duration-200 ${
                              isActive ? "text-sa-700 bg-sa-50" : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {section.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                {activeSection === "all" ? (
                  <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
                    <div className="border-b border-neutral-200 px-4 py-3 font-medium text-neutral-900">
                      <span>عرض كل الطلبات</span>
                    </div>
                    <div className="p-4 space-y-4">
                      {!loggedIn && (
                        <div className="text-sm text-neutral-600">
                          سجّل الدخول لعرض الطلبات.
                        </div>
                      )}

                      {loggedIn && (
                        <>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-end">
                            <div className="lg:col-span-1">
                              <label className="block text-sm font-medium text-neutral-700 mb-1">
                                اسم المستفيد
                              </label>
                              <select
                                className="block w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sa-300 focus:border-sa-400"
                                dir="rtl"
                                value={selectedDonorToken}
                                onChange={(e) => setSelectedDonorToken(e.target.value)}
                                disabled={loadingDonors}
                              >
                                {donors.map((d) => (
                                  <option key={d.donorIdToken} value={d.donorIdToken}>
                                    {d.name}
                                  </option>
                                ))}
                              </select>
                              {donors.length === 0 && !loadingDonors && (
                                <div className="mt-1 text-sm text-red-600">لا يوجد مستفيدون</div>
                              )}
                            </div>

                            <div className="lg:col-span-1">
                              <div className="rounded-2xl border border-gray-200 bg-sa-50 p-3 text-sm text-neutral-700">
                                عدد الطلبات:{" "}
                                <span className="font-semibold text-neutral-900">
                                  {loadingMosqueReqs ? "…" : mosqueRequests.length}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* طلبات عقود الصيانة أو النظافة */}
                          <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
                            <div className="px-4 py-3 border-b border-neutral-200 text-center">
                              <div className="text-base font-semibold text-neutral-800">
                                {serviceConfig.requestsSectionTitle}
                              </div>
                            </div>
                            <div className="p-4">
                              {mosqueReqsError && (
                                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 text-sm mb-4">
                                  {mosqueReqsError}
                                </div>
                              )}
                              {loadingMosqueReqs && (
                                <div className="text-neutral-500">جاري التحميل...</div>
                              )}
                              {!loadingMosqueReqs && mosqueRequests.length === 0 && !mosqueReqsError && (
                                <div className="text-neutral-500">لا توجد طلبات مساجد</div>
                              )}
                              {!loadingMosqueReqs && mosqueRequests.length > 0 && (
                                <div className="overflow-x-auto rounded-xl border border-neutral-200 w-full max-w-full">
                                  <table className="w-full min-w-[800px] text-sm">
                                    <thead>
                                      <tr className="text-center bg-neutral-50 text-neutral-600">
                                        <th className="py-2.5 px-3 font-medium">رقم الطلب</th>
                                        <th className="py-2.5 px-3 font-medium">رقم المسجد</th>
                                        <th className="py-2.5 px-3 font-medium">اسم المسجد</th>
                                        <th className="py-2.5 px-3 font-medium">التاريخ</th>
                                        <th className="py-2.5 px-3 font-medium">حالة الطلب</th>
                                        <th className="py-2.5 px-3 font-medium">اسم مقدم الطلب</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-neutral-200">
                                      {mosqueRequests.map((row) => (
                                        <tr key={row.supplyId} className="hover:bg-neutral-50 align-top">
                                          <td className="py-2.5 px-3 text-center text-neutral-900 whitespace-nowrap">
                                            {row.supplyName ?? row.requestName ?? `DSR-${row.supplyId}`}
                                          </td>
                                          <td className="py-2.5 px-3 text-center text-neutral-800 whitespace-nowrap">
                                            {row.number ?? "—"}
                                          </td>
                                          <td className="py-2.5 px-3 text-center text-neutral-800 max-w-[200px]">
                                            <span className="block leading-snug break-words" title={row.mosqueName ?? undefined}>{row.mosqueName ?? "—"}</span>
                                          </td>
                                          <td className="py-2.5 px-3 text-center text-neutral-700 whitespace-nowrap">
                                            {row.supplyDate ?? "—"}
                                          </td>
                                          <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                            {stageBadge(row.state ?? row.status)}
                                          </td>
                                          <td className="py-2.5 px-3 text-center text-neutral-700 max-w-[220px]">
                                            <span className="block leading-snug break-words" title={row.partnerName ?? undefined}>
                                              {row.partnerName ?? "—"}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : activeSection === "mosque" && mosqueInfo ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <section className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
                      <header className="border-b border-neutral-200 px-5 py-3">
                        <h2 className="text-base font-semibold text-neutral-900">معلومات المسجد</h2>
                      </header>
                      <div className="p-5">
                        <dl className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-6 gap-y-3">
                          <dt className="text-neutral-500">اسم المسجد</dt>
                          <dd className="text-neutral-900">{mosqueInfo.mosqueName}</dd>
                          <dt className="text-neutral-500">الرقم التسلسلي للمسجد</dt>
                          <dd className="text-neutral-900">{mosqueInfo.mosqueNumber}</dd>
                          <dt className="text-neutral-500">الرقم التسلسلي للمساهمة</dt>
                          <dd className="text-neutral-900">{mosqueInfo.contributionSerial}</dd>
                          <dt className="text-neutral-500">التصنيف</dt>
                          <dd className="text-neutral-900">{mosqueInfo.classification}</dd>
                          <dt className="text-neutral-500">المنطقة</dt>
                          <dd className="text-neutral-900">{mosqueInfo.region}</dd>
                          <dt className="text-neutral-500">المدينة</dt>
                          <dd className="text-neutral-900">{mosqueInfo.city}</dd>
                          <dt className="text-neutral-500">المركز</dt>
                          <dd className="text-neutral-900">{mosqueInfo.center}</dd>
                          <dt className="text-neutral-500">الحي</dt>
                          <dd className="text-neutral-900">{mosqueInfo.district}</dd>
                          <dt className="text-neutral-500">العنوان الكامل</dt>
                          <dd className="text-neutral-900">{mosqueInfo.fullAddress}</dd>
                          <dt className="text-neutral-500">قسم النساء</dt>
                          <dd className="text-neutral-900">{mosqueInfo.womenSection}</dd>
                        </dl>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {mosqueInfo.categoryTags.map((tag) => (
                            <span
                              key={tag.label}
                              className="px-2 py-1 rounded-full border border-gray-200 bg-gray-50 text-xs text-gray-700"
                            >
                              {tag.label}: {tag.value || "-"}
                            </span>
                          ))}
                        </div>
                      </div>
                    </section>
                    <section className="space-y-6">
                      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
                        <header className="border-b border-neutral-200 px-5 py-3">
                          <h2 className="text-base font-semibold text-neutral-900 text-center">الموقع الجغرافي</h2>
                        </header>
                        <div className="p-4">
                          {data?.latitude && data?.longitude ? (
                            <div ref={mapRef} className="h-[260px] w-full rounded-xl border border-neutral-200" />
                          ) : (
                            <div className="h-[260px] w-full rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center text-neutral-500">
                              الموقع غير متوفر
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
                        <header className="border-b border-neutral-200 px-5 py-3">
                          <h2 className="text-base font-semibold text-neutral-900 text-center">باركود</h2>
                        </header>
                        <div className="p-6 flex items-center justify-center">
                          <QRCodeCanvas value={mosqueInfo.qrValue} size={140} includeMargin />
                        </div>
                      </div>
                    </section>
                  </div>
                ) : activeSection === "applicant" ? (
                  <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
                    <div className="border-b border-neutral-200 px-4 py-3 font-medium text-neutral-900">
                      بيانات مقدم الطلب
                    </div>
                    <div className="p-4 space-y-4">
                      {!loggedIn && (
                        <div className="text-sm text-neutral-600">
                          سجّل الدخول لعرض بيانات مقدم الطلب.
                        </div>
                      )}

                      {loggedIn && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 mb-1">
                              اختر المتبرع
                            </label>
                            <div className="flex items-stretch gap-2">
                              <div className="inline-flex items-center justify-center rounded-xl border border-neutral-300 bg-white px-3 text-neutral-600">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                  <path d="M8 9a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
                                  <path d="M2 15s-1 0-1-1 1-5 7-5 7 4 7 5-1 1-1 1H2z" />
                                </svg>
                              </div>
                              <select
                                dir="rtl"
                                className="w-full rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 shadow-xs focus:border-sa-500 focus:outline-none focus:ring-2 focus:ring-sa-500"
                                value={selectedDonorToken}
                                onChange={(e) => setSelectedDonorToken(e.target.value)}
                                disabled={loadingDonors}
                              >
                                {donors.map((d) => (
                                  <option key={d.donorIdToken} value={d.donorIdToken}>
                                    {d.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {donors.length === 0 && !loadingDonors && (
                              <div className="mt-1 text-sm text-red-600">لا يوجد مستفيدون</div>
                            )}
                          </div>

                          <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                            <div className="flex flex-col gap-3 text-sm">
                              <div className="flex items-center justify-between">
                                <span className="text-neutral-500">اسم المستفيد</span>
                                <span className="font-semibold text-neutral-900">
                                  {selectedDonor?.name || "-"}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-neutral-500">الرقم التسلسلي للمساهمة</span>
                                <span className="font-semibold text-neutral-900">{donationSerial}</span>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : activeSection === "requests" ? (
                  <div className="space-y-6">
                    <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
                      <div className="border-b border-neutral-200 px-4 py-3 font-medium text-neutral-900">
                        تفاصيل الطلب
                      </div>
                      <div className="p-4">
                        {!loggedIn && (
                          <div className="text-sm text-neutral-600">
                            سجّل الدخول لعرض تفاصيل الطلب.
                          </div>
                        )}
                        {loggedIn && (
                          <SupplyRequestsSection
                            requests={filteredSupplyRequests}
                            selectedReq={selectedSupplyReq}
                            onSelect={setSelectedSupplyReq}
                            loadingRequests={loadingMosqueReqs}
                            stageLabel={(s) => stageBadge(s)}
                            attachmentsRequired={false}
                            isDraft={supplyIsDraft}
                            isDirectDonor={supplyIsDirectDonor}
                            hasFinancial={supplyHasFinancial}
                            hasPoA={supplyHasPoA}
                            requirementsMet={supplyRequirementsMet}
                            confirmChecked={supplyDeclarationAccepted}
                            onConfirmChange={setSupplyDeclarationAccepted}
                            onSubmitRequest={onSubmitSupplyRequest}
                            submittingReq={supplySubmitting}
                            onToggleEdit={() =>
                              setSupplyEditing((prev) => !prev)
                            }
                            editing={supplyEditing}
                            docType={supplyDocType}
                            setDocType={setSupplyDocType}
                            fileInputRef={supplyFileInputRef}
                            uploadBusy={supplyUploadBusy}
                            setUploadBusy={setSupplyUploadBusy}
                            uploadErr={supplyUploadErr}
                            setUploadErr={setSupplyUploadErr}
                            MAX_MB={MAX_MB}
                            doUpload={supplyDoUpload}
                            filesLoading={supplyFilesLoading}
                            files={supplyFiles}
                            previews={supplyPreviews}
                            downloadByDonor={supplyDownloadByDonor}
                            submitErr={supplySubmitErr}
                            submitOk={supplySubmitOk}
                            onGoToMyRequests={() =>
                              navigate(
                                `/my-requests?donor=${encodeURIComponent(selectedDonorToken)}&tab=${serviceConfig.tabId}`
                              )
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm p-6 text-neutral-600">
                    سيتم إضافة محتوى هذا القسم لاحقاً.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <NotBenefactorModal open={showNotBenefactorModal} onClose={() => setShowNotBenefactorModal(false)} isLoggedIn={true} />
    </div>
  );
};

export default MosqueDonationRequest;
